'use client';

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CmsImage from '@/components/ui/CmsImage';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { ContactPageData } from '@/types/contact';
import ContactHeroButton from './ContactHeroButton';

interface ContactHeroProps {
  data: ContactPageData;
}

export default function ContactHero({ data }: ContactHeroProps) {
  const heroRef = useRef<HTMLElement>(null);
  const heroImgRef = useRef<HTMLDivElement>(null);

  const title = data.bannerHeading?.trim() || data.pageTitle?.trim() || '';
  const subtitle = stripHtml(data.bannerSubheading);
  const bgLightText = data.bgLightText?.trim() || '';
  const heroImageUrl = getStrapiMediaUrl(data.bgMedia, 'large') || MEDIA_PLACEHOLDER;
  const heroImageAlt = data.bgMedia?.alternativeText || data.bgMedia?.caption || title;
  const titleWords = title.split(/\s+/).filter(Boolean);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      if (heroImgRef.current) {
        gsap.to(heroImgRef.current, {
          yPercent: 18,
          ease: 'none',
          scrollTrigger: {
            trigger: heroImgRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 1.4,
          },
        });
      }

      document.querySelectorAll('.ct-word').forEach((w, i) => {
        gsap.fromTo(
          w,
          { autoAlpha: 0, y: 40, clipPath: 'inset(100% 0% 0% 0%)' },
          {
            autoAlpha: 1,
            y: 0,
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 0.75,
            ease: 'power3.out',
            delay: 0.2 + i * 0.08,
          },
        );
      });
      gsap.fromTo(
        '.ct-sub',
        { autoAlpha: 0, y: 16 },
        { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out', delay: 0.55 },
      );
      gsap.fromTo(
        '.ct-chip',
        { autoAlpha: 0, y: 14, scale: 0.92 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.5,
          ease: 'back.out(1.4)',
          stagger: 0.1,
          delay: 0.7,
        },
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative flex min-h-screen flex-col overflow-hidden text-white max-sm:min-h-[60vh]"
    >
      <div ref={heroImgRef} className="absolute inset-0 scale-110">
        <CmsImage
          src={heroImageUrl}
          alt={heroImageAlt}
          fill
          className="object-cover object-center"
          priority
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
      </div>

      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(160deg, rgba(4,8,16,0.95) 0%, rgba(8,16,30,0.88) 45%, rgba(6,12,24,0.72) 100%)',
        }}
        aria-hidden
      />

      <div className="absolute inset-x-0 top-0 h-0.5 bg-[#E31E24]" aria-hidden />

      {bgLightText ? (
        <p
          className="pointer-events-none absolute -bottom-4 right-0 select-none text-[12rem] font-black leading-none text-white/[0.03] md:text-[16rem]"
          aria-hidden
        >
          {bgLightText}
        </p>
      ) : null}

      <div
        className="relative flex flex-1 flex-col justify-end"
        style={{ paddingTop: 'var(--site-header-height)' }}
      >
        <div className="mx-auto w-full container max-sm:px-4 pb-14 md:pb-20">
          {title ? (
            <h1 className="hero-title max-w-[780px] text-white">
              {titleWords.map((word, i) => (
                <span
                  key={i}
                  className="ct-word mr-[0.22em] inline-block"
                  style={{ clipPath: 'inset(100% 0% 0% 0%)' }}
                >
                  {word}
                </span>
              ))}
              <span
                className="ct-word inline-block text-[#E31E24]"
                style={{ clipPath: 'inset(100% 0% 0% 0%)' }}
              >
                .
              </span>
            </h1>
          ) : null}

          {subtitle ? (
            <p className="ct-sub hero-subtitle mt-5 max-w-[460px] text-white/50">{subtitle}</p>
          ) : null}

          {(data.button ?? []).length > 0 ? (
            <div className="mt-8 flex flex-wrap gap-3">
              {(data.button ?? []).map((button) => (
                <ContactHeroButton key={button.id} button={button} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
